/**
 * B.3.c — Admin-consent landing detection (pure).
 *
 * Microsoft's `/adminconsent` endpoint redirects to the app reg's exact
 * registered SPA `redirectUri` (in dev: `http://localhost:5091`) with
 * `?admin_consent=True&tenant=<id>[&error=...]` appended on completion.
 *
 * This helper extracts that state. The banner in `__root.tsx` reads it,
 * shows a confirmation, and offers deeplinks back to whichever flow
 * stashed an intent in sessionStorage before kicking off admin-consent
 * (e.g. F18 onboarding wizard).
 *
 * Memory: feedback_admin_consent_landing_ux — pair every adminconsent
 * kick-off with its return handler. Don't drop on demo desktop.
 */

export const ADMIN_CONSENT_INTENT_KEY = 'repancy.adminConsent.intent';

export interface AdminConsentLanding {
  /** True if the URL carries the admin_consent query string from Entra. */
  detected: boolean;
  /** Decoded tenant id from the URL, or null if absent/invalid. */
  tenantId: string | null;
  /** Microsoft sets this to "True" on success. */
  success: boolean;
  /**
   * Error info passed back by Entra on failure (e.g. AADSTS65004 — user
   * declined). null when success is true.
   */
  errorCode: string | null;
  errorDescription: string | null;
}

/** Decode admin-consent state from a query string (no DOM access). */
export function parseAdminConsentLanding(search: string): AdminConsentLanding {
  const params = new URLSearchParams(search);
  const flag = params.get('admin_consent');
  if (flag === null) {
    return {
      detected: false,
      tenantId: null,
      success: false,
      errorCode: null,
      errorDescription: null,
    };
  }
  return {
    detected: true,
    tenantId: params.get('tenant'),
    success: flag.toLowerCase() === 'true',
    errorCode: params.get('error'),
    errorDescription: params.get('error_description'),
  };
}

export interface AdminConsentIntent {
  /** Slug or onboarding-wizard step the flow originated from. */
  origin: string;
  /** Route to send the user back to once admin-consent completes. */
  returnTo: string;
  /** Optional portal slug suggestions for the success banner deeplinks. */
  knownSlugs?: string[];
  /** ISO timestamp the intent was stashed (for staleness checks). */
  startedAt: string;
}

export function isAdminConsentIntent(value: unknown): value is AdminConsentIntent {
  if (typeof value !== 'object' || value === null) {return false;}
  const v = value as Record<string, unknown>;
  return (
    typeof v.origin === 'string' &&
    typeof v.returnTo === 'string' &&
    typeof v.startedAt === 'string' &&
    (v.knownSlugs === undefined || Array.isArray(v.knownSlugs))
  );
}

/**
 * Read the intent record a flow stashed before kicking off admin-consent.
 * Returns null if absent or stale (>30 minutes old).
 *
 * Stash via `sessionStorage.setItem(ADMIN_CONSENT_INTENT_KEY, JSON.stringify({...}))`
 * right before navigating to the `/adminconsent` URL.
 */
export function readAdminConsentIntent(
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
  nowMs: number = Date.now(),
): AdminConsentIntent | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(ADMIN_CONSENT_INTENT_KEY);
  } catch {
    return null;
  }
  if (raw === null) {return null;}
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isAdminConsentIntent(parsed)) {return null;}
  const startedMs = Date.parse(parsed.startedAt);
  if (Number.isNaN(startedMs) || nowMs - startedMs > 30 * 60 * 1000) {
    try {storage.removeItem(ADMIN_CONSENT_INTENT_KEY);} catch {/* ignore */}
    return null;
  }
  return parsed;
}

export function clearAdminConsentIntent(
  storage: Pick<Storage, 'removeItem'>,
): void {
  try {
    storage.removeItem(ADMIN_CONSENT_INTENT_KEY);
  } catch {
    // ignore
  }
}
